/**
 * "Open in Browser" banner for in-app webviews.
 *
 * @origin: v2 | @since: 8100552a | @used-by: ALL | @purpose: prompts
 * customers who arrived inside a partner's embedded webview to open the
 * journey in a real browser, since camera/location/OTP flows are unreliable
 * inside webviews. Ported verbatim (shadcn/Tailwind already matched v3's
 * conventions); mounted on HomePreApproved.tsx, the pre-login entry page,
 * per recon Q8.
 *
 * Shown at the top of a journey entry page when the customer arrived from a
 * partner's native app (PetPooja, WhatsApp, Instagram, ...) and is therefore
 * inside an embedded webview rather than a real browser. See
 * `src/lib/inAppBrowser.ts` for why that matters (cookie/storage siloing,
 * unreliable camera + location permissions, no address bar to verify the
 * domain).
 *
 * DESIGN DECISIONS
 * ================
 *
 * [1] IT NEVER AUTO-REDIRECTS. The escape is always an explicit tap.
 *     Auto-firing on mount would mean that any false positive in webview
 *     detection bounces a legitimate mobile-Chrome visitor, or fires an intent
 *     that fails and leaves them staring at a stalled page. On an acquisition
 *     landing page that is a lost customer. A banner they can ignore costs
 *     nothing by comparison.
 *
 * [2] IT RENDERS NOTHING OUTSIDE A DETECTED WEBVIEW. Ordinary browsers -- and
 *     desktop -- see no banner at all, so the page is unchanged for the
 *     majority of visitors.
 *
 * [3] IT IS DISMISSIBLE, AND THE DISMISSAL STICKS for the session. A customer
 *     who is happy in the webview should not have to re-dismiss on every
 *     render or navigation.
 *
 * [4] ANDROID AND iOS GET DIFFERENT UX, because the platforms genuinely differ:
 *       * Android CAN be handed to the OS chooser via `intent://`, so it gets a
 *         real button. Because a webview may still swallow the intent, the
 *         manual copy-link fallback stays available afterwards.
 *       * iOS CANNOT be redirected out of a webview by web content at all --
 *         there is no API for it. Pretending otherwise with a button that does
 *         nothing would be worse than being honest, so iOS gets copy-link plus
 *         the host app's own "Open in Safari" instructions.
 *
 * The URL it opens is `window.location.href`, so the `?bap=PetPooja` attribution
 * parameter survives the jump.
 */
import React from 'react';
import { ExternalLink, Copy, Check, X, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    attemptOpenInDeviceBrowser,
    copyTextToClipboard,
    getCurrentAbsoluteUrl,
    getMobilePlatform,
    isInAppBrowser,
} from '@/lib/inAppBrowser';

/**
 * sessionStorage (not localStorage) so the dismissal lasts for this visit only.
 * A customer who returns days later in the webview should be reminded again.
 */
const DISMISS_KEY = 'open_in_browser_banner_dismissed';

interface OpenInBrowserBannerProps {
    /**
     * Optional context line, e.g. "for camera and document upload to work
     * reliably". Kept injectable so the same banner can front different
     * journeys without hard-coding one journey's wording.
     */
    reason?: string;
}

const OpenInBrowserBanner: React.FC<OpenInBrowserBannerProps> = ({ reason }) => {
    // Detection runs in an effect rather than in the initial state so this
    // component is SSR/prerender-safe and never touches `navigator` during the
    // first render pass.
    const [shouldShow, setShouldShow] = React.useState(false);
    const [platform, setPlatform] = React.useState<'android' | 'ios' | 'other'>('other');
    const [copied, setCopied] = React.useState(false);
    const [attempted, setAttempted] = React.useState(false);

    React.useEffect(() => {
        let dismissed = false;
        try {
            dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
        } catch {
            // Private-mode webviews can throw on sessionStorage access; treat
            // that as "not dismissed" rather than failing to render.
        }
        if (dismissed) return;

        if (isInAppBrowser()) {
            setPlatform(getMobilePlatform());
            setShouldShow(true);
        }
    }, []);

    const dismiss = () => {
        setShouldShow(false);
        try {
            sessionStorage.setItem(DISMISS_KEY, '1');
        } catch {
            // Non-fatal: the banner still closes for this render.
        }
    };

    const handleOpenInBrowser = () => {
        const url = getCurrentAbsoluteUrl();
        // Mark the attempt BEFORE navigating: if the intent succeeds this
        // component is gone anyway, and if it is swallowed the extra guidance
        // is already on screen.
        setAttempted(true);
        const invoked = attemptOpenInDeviceBrowser(url);
        if (!invoked) {
            // iOS, or a non-mobile UA that still looks like a webview. Copying
            // is the only thing that actually helps here.
            void handleCopy();
        }
    };

    const handleCopy = async () => {
        const ok = await copyTextToClipboard(getCurrentAbsoluteUrl());
        if (ok) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2500);
        }
        setAttempted(true);
    };

    if (!shouldShow) return null;

    const isAndroid = platform === 'android';

    return (
        <div
            role="region"
            aria-label="Open this page in your browser"
            className="bg-amber-50 border-b-2 border-amber-300 px-4 py-3"
        >
            <div className="container mx-auto max-w-3xl">
                <div className="flex items-start gap-3">
                    <ShieldCheck
                        className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                        aria-hidden="true"
                    />

                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-amber-900">
                            For the best experience, open this page in your browser
                        </p>
                        <p className="mt-0.5 text-xs text-amber-800">
                            {reason ??
                                'You are viewing this inside an app. Chrome or Safari keeps you signed in and lets camera and location work reliably.'}
                        </p>

                        <div className="mt-2.5 flex flex-wrap items-center gap-2">
                            {isAndroid && (
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleOpenInBrowser}
                                    className="bg-amber-600 text-white hover:bg-amber-700"
                                >
                                    <ExternalLink className="mr-1.5 h-4 w-4" />
                                    Open in Browser
                                </Button>
                            )}

                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={handleCopy}
                                className="border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
                            >
                                {copied ? (
                                    <>
                                        <Check className="mr-1.5 h-4 w-4" />
                                        Link copied
                                    </>
                                ) : (
                                    <>
                                        <Copy className="mr-1.5 h-4 w-4" />
                                        Copy link
                                    </>
                                )}
                            </Button>
                        </div>

                        {/*
                          iOS gets the instructions immediately, because there is
                          no button that could work for it. Android only gets
                          them after an attempt, as a fallback for webviews that
                          swallow the intent.
                        */}
                        {(!isAndroid || attempted) && (
                            <p className="mt-2 text-xs leading-relaxed text-amber-800">
                                {isAndroid ? (
                                    <>
                                        Nothing happened? Tap the{' '}
                                        <span className="font-semibold">⋮</span> menu at the
                                        top of this app and choose{' '}
                                        <span className="font-semibold">
                                            &ldquo;Open in browser&rdquo;
                                        </span>
                                        , or paste the copied link into Chrome.
                                    </>
                                ) : (
                                    <>
                                        Tap the{' '}
                                        <span className="font-semibold">
                                            share or &ldquo;&hellip;&rdquo;
                                        </span>{' '}
                                        button in this app and choose{' '}
                                        <span className="font-semibold">
                                            &ldquo;Open in Safari&rdquo;
                                        </span>
                                        , or paste the copied link into Safari.
                                    </>
                                )}
                            </p>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label="Dismiss"
                        className="-mr-1 shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OpenInBrowserBanner;
